import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, InputGroup, Alert, Modal, Tabs, Tab } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaUserAlt, FaGraduationCap, FaLock, FaImage, FaSyncAlt, FaPhone, FaEnvelope, FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './Register.css';

const Register = () => {
  const mainColor = '#006186';
  
  // Состояния для формы
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    university: 'oshTU',
    direction: '',
    customDirection: '',
    specialty: '',
    customSpecialty: '',
    yearOfAdmission: '',
    yearOfGraduation: '',
    photo: '',
    position: '',
    workplace: '',
    location: '',
    linkedin: '',
    facebook: '',
    registerMethod: 'email'
  });

  // Состояния для показа/скрытия пароля
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Состояния для подтверждения
  const [showVerification, setShowVerification] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [phoneVerified, setPhoneVerified] = useState(false);

  const [directions, setDirections] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('email');

  useEffect(() => {
    fetchDirections();
  }, []);

  // Таймер для повторной отправки
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(t => t - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  // Проверка совпадения паролей
  useEffect(() => {
    if (formData.confirmPassword) {
      setPasswordMatch(formData.password === formData.confirmPassword);
    }
  }, [formData.password, formData.confirmPassword]);

  const fetchDirections = async () => {
    try {
      const response = await fetch('/api/directions');
      const data = await response.json();
      setDirections(Array.isArray(data) ? data : []);
    } catch (err) { 
      console.error('Ошибка загрузки направлений');
      setDirections([]);
    }
  };

  useEffect(() => {
    const fetchSpecs = async () => {
      if (formData.direction && formData.direction !== 'custom') {
        try {
          const response = await fetch(`/api/specialties/${encodeURIComponent(formData.direction)}`);
          const data = await response.json();
          setSpecialties(Array.isArray(data) ? data : []);
        } catch (err) { 
          console.error('Ошибка специальностей');
          setSpecialties([]);
        }
      } else {
        setSpecialties([]);
      }
    };
    fetchSpecs();
  }, [formData.direction]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file' && name === 'photo') {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, photo: reader.result }));
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phone: value }));
  };

  const handleVerificationCodeChange = (index, value) => {
    if (value.length <= 1) {
      const newCode = [...verificationCode];
      newCode[index] = value;
      setVerificationCode(newCode);

      if (value && index < 5) {
        document.getElementById(`code-${index + 1}`).focus();
      }
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData(prev => ({ 
      ...prev, 
      registerMethod: tab,
      email: '',
      phone: ''
    }));
    setPhoneVerified(false);
  };

  // Функция для отправки SMS с кодом подтверждения
  const sendVerificationSMS = async (phone) => {
    try {
      const cleanPhone = phone.replace(/\D/g, '');
      
      const response = await fetch('/api/auth/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'SMS с кодом отправлен на ваш номер' });
        setTimer(600);
        setCanResend(false);
        return true;
      } else {
        setMessage({ type: 'danger', text: data.error || 'Ошибка отправки SMS' });
        return false;
      }
    } catch (err) {
      console.error('Ошибка отправки SMS:', err);
      setMessage({ type: 'danger', text: 'Ошибка соединения с сервером' });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!passwordMatch) {
      setMessage({ type: 'danger', text: 'Пароли не совпадают' });
      return;
    }

    if (formData.password.length < 6) {
      setMessage({ type: 'danger', text: 'Пароль должен быть не менее 6 символов' });
      return;
    }

    if (activeTab === 'email' && !formData.email) {
      setMessage({ type: 'danger', text: 'Введите email' });
      return;
    }

    if (activeTab === 'phone' && (!formData.phone || formData.phone.length < 10)) {
      setMessage({ type: 'danger', text: 'Введите корректный номер телефона' });
      return;
    }

    if (formData.university === 'oshTU') {
      if (!formData.direction) {
        setMessage({ type: 'danger', text: 'Выберите направление' });
        return;
      }
      if (!formData.specialty && formData.direction !== 'custom') {
        setMessage({ type: 'danger', text: 'Выберите специальность' });
        return;
      }
      if (!formData.yearOfAdmission) {
        setMessage({ type: 'danger', text: 'Введите год поступления' });
        return;
      }
      if (!formData.yearOfGraduation) {
        setMessage({ type: 'danger', text: 'Введите год выпуска' });
        return;
      }
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    const isOshTU = formData.university === 'oshTU';
    
    const finalDirection = formData.direction === 'custom' ? formData.customDirection : formData.direction;
    const finalSpecialty = formData.specialty === 'custom' ? formData.customSpecialty : formData.specialty;
    const fullName = `${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim();

    if (activeTab === 'phone') {
      const smsSent = await sendVerificationSMS(formData.phone);
      if (smsSent) {
        sessionStorage.setItem('pendingRegistration', JSON.stringify({
          formData,
          finalDirection,
          finalSpecialty,
          fullName,
          isOshTU
        }));
        setShowVerification(true);
        setLoading(false);
      } else {
        setLoading(false);
      }
      return;
    }

    await completeRegistration({
      formData,
      finalDirection,
      finalSpecialty,
      fullName,
      isOshTU,
      phoneVerified: false
    });
  };

  const completeRegistration = async (data) => {
    const { formData, finalDirection, finalSpecialty, fullName, isOshTU, phoneVerified } = data;

    const payload = {
      fio: fullName,
      email: formData.email || null,
      phone: formData.phone || null,
      password: formData.password,
      university: isOshTU ? 'ОшТУ' : 'Другой ВУЗ',
      direction: isOshTU ? finalDirection : null,
      specialty: isOshTU ? finalSpecialty : null,
      gradYear: isOshTU ? formData.yearOfGraduation : null,
      profilePic: formData.photo,
      phoneVerified: phoneVerified
    };

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        setUserId(result.user.id);
        
        if (isOshTU) {
          try {
            const requestPayload = {
              fio: fullName,
              direction: finalDirection,
              specialty: finalSpecialty,
              graduation_year: parseInt(formData.yearOfGraduation),
              performance_year: parseInt(formData.yearOfAdmission),
              image_url: formData.photo,
              position: formData.position || '',
              workplace: formData.workplace || '',
              location: formData.location || '',
              linkedin: formData.linkedin || '',
              facebook: formData.facebook || ''
            };

            const requestResponse = await fetch('/api/alumni/request', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${result.token}`
              },
              body: JSON.stringify(requestPayload)
            });

            if (requestResponse.ok) {
              setShowSuccessModal(true);
            }
          } catch (err) {
            console.error('Ошибка создания заявки:', err);
          }
        }

        localStorage.setItem('adminToken', result.token);
        localStorage.setItem('token', result.token);
        localStorage.setItem('adminUser', JSON.stringify(result.user));
        
        setMessage({ 
          type: 'success', 
          text: isOshTU 
            ? 'Регистрация успешна! Заявка отправлена администратору.' 
            : 'Регистрация успешна! Перенаправление...' 
        });
        
        if (!isOshTU) {
          setTimeout(() => {
            window.location.replace('/profile');
          }, 2000);
        }
      } else {
        setMessage({ type: 'danger', text: result.error });
      }
    } catch (err) {
      console.error('Ошибка регистрации:', err);
      setMessage({ type: 'danger', text: 'Ошибка соединения с сервером' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    
    if (code.length !== 6) {
      setMessage({ type: 'danger', text: 'Введите 6-значный код' });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formData.phone.replace(/\D/g, ''),
          code: code
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Телефон подтвержден!' });
        setShowVerification(false);
        setPhoneVerified(true);
        
        const pendingData = JSON.parse(sessionStorage.getItem('pendingRegistration'));
        if (pendingData) {
          await completeRegistration({
            ...pendingData,
            phoneVerified: true
          });
          sessionStorage.removeItem('pendingRegistration');
        }
      } else {
        setMessage({ type: 'danger', text: data.error || 'Неверный код' });
      }
    } catch (err) {
      console.error('Ошибка подтверждения:', err);
      setMessage({ type: 'danger', text: 'Ошибка подтверждения' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    setLoading(true);

    try {
      const response = await fetch('/api/auth/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone.replace(/\D/g, '') })
      });

      const data = await response.json();

      if (response.ok) {
        setTimer(600);
        setCanResend(false);
        setMessage({ type: 'success', text: 'Новый код отправлен' });
      } else {
        setMessage({ type: 'danger', text: data.error });
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Ошибка отправки' });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="register-page bg-white" style={{ marginTop: '10px', minHeight: '100vh' }}>
      <Container className="pb-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="p-4 text-white text-center fw-bold text-uppercase" style={{ backgroundColor: mainColor, letterSpacing: '1px' }}>
                Регистрация выпускника ОшТУ
              </div>
              
              <Card.Body className="p-4 p-md-5 bg-light-subtle">
                {message.text && (
                  <Alert variant={message.type} className="mb-4">
                    {message.text}
                  </Alert>
                )}

                <Tabs
                  activeKey={activeTab}
                  onSelect={handleTabChange}
                  className="mb-4"
                  fill
                >
                  <Tab eventKey="email" title="Email">
                    <div className="p-3">
                      <FaEnvelope size={24} style={{ color: mainColor }} className="me-2" />
                      <span>Регистрация через email (без подтверждения)</span>
                    </div>
                  </Tab>
                  <Tab eventKey="phone" title="Телефон">
                    <div className="p-3">
                      <FaPhone size={24} style={{ color: mainColor }} className="me-2" />
                      <span>Регистрация через телефон (с SMS подтверждением)</span>
                    </div>
                  </Tab>
                </Tabs>

                <Form onSubmit={handleSubmit}>
                  <h5 className="fw-bold mb-4 d-flex align-items-center" style={{ color: mainColor }}>
                    <FaUserAlt className="me-2" /> Личные данные
                  </h5>
                  
                  <Row className="g-3 mb-4">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Фамилия *</Form.Label>
                        <Form.Control 
                          name="lastName" 
                          className="py-2 border-0 shadow-sm" 
                          onChange={handleChange} 
                          required 
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Имя *</Form.Label>
                        <Form.Control 
                          name="firstName" 
                          className="py-2 border-0 shadow-sm" 
                          onChange={handleChange} 
                          required 
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Отчество</Form.Label>
                        <Form.Control 
                          name="middleName" 
                          className="py-2 border-0 shadow-sm" 
                          onChange={handleChange} 
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mb-4">
                    <Col md={6}>
                      {activeTab === 'email' ? (
                        <Form.Group>
                          <Form.Label className="small fw-bold">
                            <FaEnvelope className="me-1" /> Email *
                          </Form.Label>
                          <Form.Control 
                            type="email" 
                            name="email" 
                            className="py-2 border-0 shadow-sm" 
                            onChange={handleChange} 
                            required={activeTab === 'email'}
                            value={formData.email}
                          />
                        </Form.Group>
                      ) : (
                        <Form.Group>
                          <Form.Label className="small fw-bold">
                            <FaEnvelope className="me-1" /> Email (необязательно)
                          </Form.Label>
                          <Form.Control 
                            type="email" 
                            name="email" 
                            className="py-2 border-0 shadow-sm" 
                            onChange={handleChange} 
                            value={formData.email}
                          />
                        </Form.Group>
                      )}
                    </Col>
                    <Col md={6}>
                      {activeTab === 'phone' ? (
                        <Form.Group>
                          <Form.Label className="small fw-bold">
                            <FaPhone className="me-1" /> Номер телефона *
                          </Form.Label>
                          <PhoneInput
                            country={'kg'}
                            value={formData.phone}
                            onChange={handlePhoneChange}
                            inputStyle={{
                              width: '100%',
                              height: '38px',
                              border: 'none',
                              boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)'
                            }}
                            containerStyle={{ width: '100%' }}
                            required={activeTab === 'phone'}
                          />
                          <Form.Text className="text-muted">
                            На этот номер придет SMS с кодом подтверждения
                          </Form.Text>
                        </Form.Group>
                      ) : (
                        <Form.Group>
                          <Form.Label className="small fw-bold">
                            <FaPhone className="me-1" /> Номер телефона (необязательно)
                          </Form.Label>
                          <PhoneInput
                            country={'kg'}
                            value={formData.phone}
                            onChange={handlePhoneChange}
                            inputStyle={{
                              width: '100%',
                              height: '38px',
                              border: 'none',
                              boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)'
                            }}
                            containerStyle={{ width: '100%' }}
                          />
                        </Form.Group>
                      )}
                    </Col>
                  </Row>

                  <Row className="g-3 mb-4 border-bottom pb-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Пароль *</Form.Label>
                        <InputGroup className="shadow-sm rounded">
                          <Form.Control 
                            type={showPassword ? 'text' : 'password'} 
                            name="password" 
                            className="py-2 border-0" 
                            onChange={handleChange} 
                            required 
                            minLength="6"
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                            className="border-0 bg-white"
                            style={{ zIndex: 0 }}
                          >
                            {showPassword ? <FaEyeSlash className="text-muted" /> : <FaEye className="text-muted" />}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Подтверждение пароля *</Form.Label>
                        <InputGroup className="shadow-sm rounded">
                          <Form.Control 
                            type={showConfirmPassword ? 'text' : 'password'} 
                            name="confirmPassword" 
                            className={`py-2 border-0 ${!passwordMatch ? 'is-invalid' : ''}`}
                            onChange={handleChange} 
                            required 
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="border-0 bg-white"
                            style={{ zIndex: 0 }}
                          >
                            {showConfirmPassword ? <FaEyeSlash className="text-muted" /> : <FaEye className="text-muted" />}
                          </Button>
                        </InputGroup>
                        {!passwordMatch && (
                          <Form.Text className="text-danger">
                            Пароли не совпадают
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <h5 className="fw-bold mb-4 d-flex align-items-center" style={{ color: mainColor }}>
                    <FaGraduationCap className="me-2" /> Образование
                  </h5>
                  
                  <Form.Group className="mb-4 d-flex gap-4 p-3 bg-white rounded shadow-sm">
                    <Form.Check 
                      type="radio" 
                      label="Выпускник ОшТУ" 
                      name="university" 
                      value="oshTU" 
                      checked={formData.university === 'oshTU'} 
                      onChange={handleChange} 
                    />
                    <Form.Check 
                      type="radio" 
                      label="Другой ВУЗ" 
                      name="university" 
                      value="other" 
                      checked={formData.university === 'other'} 
                      onChange={handleChange} 
                    />
                  </Form.Group>

                  {formData.university === 'oshTU' && (
                    <>
                      <Alert variant="info" className="mt-3 mb-4 small">
                        <FaGraduationCap className="me-2" />
                        После регистрации ваша заявка будет отправлена администратору на проверку.
                        После подтверждения вы станете официальным членом ассоциации выпускников ОшТУ.
                      </Alert>

                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                        <Row className="g-3 mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Направление *</Form.Label>
                              <Form.Select 
                                name="direction" 
                                value={formData.direction} 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                required
                              >
                                <option value="">Выберите...</option>
                                {directions.map((d, i) => (
                                  <option key={i} value={d}>{d}</option>
                                ))}
                                <option value="custom" className="text-primary fw-bold">
                                  + Моего направления нет
                                </option>
                              </Form.Select>
                              {formData.direction === 'custom' && (
                                <Form.Control 
                                  name="customDirection" 
                                  placeholder="Введите название направления" 
                                  className="mt-2 border-primary" 
                                  onChange={handleChange} 
                                  required 
                                />
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Специальность *</Form.Label>
                              <Form.Select 
                                name="specialty" 
                                value={formData.specialty} 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                disabled={!formData.direction || formData.direction === 'custom'}
                                required
                              >
                                <option value="">Выберите...</option>
                                {specialties.map((s, i) => (
                                  <option key={i} value={s}>{s}</option>
                                ))}
                                <option value="custom" className="text-primary fw-bold">
                                  + Моей специальности нет
                                </option>
                              </Form.Select>
                              {(formData.specialty === 'custom' || formData.direction === 'custom') && (
                                <Form.Control 
                                  name="customSpecialty" 
                                  placeholder="Введите название специальности" 
                                  className="mt-2 border-primary" 
                                  onChange={handleChange} 
                                  required 
                                />
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row className="g-3 mb-4">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Год поступления *</Form.Label>
                              <Form.Control 
                                type="number" 
                                name="yearOfAdmission" 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                required 
                                min="1950" 
                                max="2030"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Год выпуска *</Form.Label>
                              <Form.Control 
                                type="number" 
                                name="yearOfGraduation" 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                required 
                                min="1950" 
                                max="2030"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <h5 className="fw-bold mb-4 d-flex align-items-center" style={{ color: mainColor }}>
                          <FaUserAlt className="me-2" /> Дополнительная информация
                        </h5>

                        <Row className="g-3 mb-4">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Должность</Form.Label>
                              <Form.Control 
                                type="text" 
                                name="position" 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                value={formData.position}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Место работы</Form.Label>
                              <Form.Control 
                                type="text" 
                                name="workplace" 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                value={formData.workplace}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="g-3 mb-4">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Город/Страна</Form.Label>
                              <Form.Control 
                                type="text" 
                                name="location" 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                value={formData.location}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">LinkedIn</Form.Label>
                              <Form.Control 
                                type="url" 
                                name="linkedin" 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                value={formData.linkedin}
                                placeholder="https://linkedin.com/in/..."
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="g-3 mb-4">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">Facebook</Form.Label>
                              <Form.Control 
                                type="url" 
                                name="facebook" 
                                className="py-2 border-0 shadow-sm" 
                                onChange={handleChange} 
                                value={formData.facebook}
                                placeholder="https://facebook.com/..."
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="small fw-bold">
                                <FaImage className="me-1" /> Фотография
                              </Form.Label>
                              <Form.Control 
                                type="file" 
                                name="photo" 
                                className="py-2 border-0 shadow-sm bg-white" 
                                onChange={handleChange} 
                                accept="image/*"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </motion.div>
                    </>
                  )}

                  <Form.Check 
                    className="mb-4 small text-muted" 
                    type="checkbox" 
                    label="Я даю согласие на обработку персональных данных" 
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    required 
                  />

                  <Button 
                    type="submit" 
                    className="w-100 py-3 border-0 fw-bold shadow" 
                    style={{ backgroundColor: mainColor }}
                    disabled={loading}
                  >
                    {loading ? 'Отправка...' : activeTab === 'phone' ? 'ПОЛУЧИТЬ КОД' : 'ЗАРЕГИСТРИРОВАТЬСЯ'}
                  </Button>

                  <div className="text-center mt-4">
                    <p className="text-muted small">
                      Уже есть аккаунт? <a href="/login" style={{ color: mainColor }}>Войти</a>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Модальное окно подтверждения телефона */}
      <Modal 
        show={showVerification} 
        centered 
        onHide={() => setShowVerification(false)}
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: mainColor }}>
            Подтверждение телефона
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          <div className="mb-4">
            <FaPhone size={50} style={{ color: mainColor }} />
          </div>
          
          <p className="mb-3">
            Мы отправили 6-значный код на номер<br />
            <strong>{formData.phone}</strong>
          </p>

          <div className="d-flex justify-content-center gap-2 mb-4">
            {verificationCode.map((digit, index) => (
              <Form.Control
                key={index}
                id={`code-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleVerificationCodeChange(index, e.target.value)}
                className="text-center fw-bold"
                style={{ width: '50px', height: '50px', fontSize: '20px' }}
              />
            ))}
          </div>

          {timer > 0 && (
            <p className="text-muted small mb-3">
              Код действителен: {formatTime(timer)}
            </p>
          )}

          <div className="d-flex gap-2 justify-content-center">
            <Button 
              variant="outline-secondary" 
              onClick={handleResendCode}
              disabled={!canResend || loading}
            >
              Отправить снова
            </Button>
            <Button 
              style={{ backgroundColor: mainColor, borderColor: mainColor }}
              onClick={handleVerifyCode}
              disabled={loading}
            >
              {loading ? 'Проверка...' : 'Подтвердить'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Модальное окно успешной отправки заявки */}
      <Modal 
        show={showSuccessModal} 
        centered 
        onHide={() => {
          setShowSuccessModal(false);
          window.location.replace('/profile');
        }}
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success">
            <FaCheckCircle className="me-2" /> Заявка отправлена!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          <div className="mb-4">
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              background: '#d4edda',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <FaGraduationCap size={40} color="#28a745" />
            </div>
          </div>
          
          <h5 className="mb-3">Спасибо за регистрацию!</h5>
          
          <p className="text-muted mb-3">
            Ваша заявка на добавление в ассоциацию выпускников ОшТУ 
            отправлена администратору на рассмотрение.
          </p>
          
          <div className="bg-light p-3 rounded mb-3 text-start">
            <p className="mb-1"><strong>ФИО:</strong> {`${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim()}</p>
            <p className="mb-1"><strong>Направление:</strong> {
              formData.direction === 'custom' ? formData.customDirection : formData.direction
            }</p>
            <p className="mb-1"><strong>Специальность:</strong> {
              formData.specialty === 'custom' ? formData.customSpecialty : formData.specialty
            }</p>
            <p className="mb-1"><strong>Год выпуска:</strong> {formData.yearOfGraduation}</p>
          </div>
          
          <p className="small text-muted mb-3">
            После проверки данных администратором, вы получите уведомление 
            и сможете войти в личный кабинет.
          </p>

          <Button 
            style={{ backgroundColor: mainColor, borderColor: mainColor }}
            onClick={() => {
              setShowSuccessModal(false);
              window.location.replace('/profile');
            }}
            className="px-4"
          >
            Понятно
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Register;